import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { THEME_PREFERENCES_VERSION } from './constants';
import { themePreferencesService } from './service';
import { readThemePreferencesFromStorage, writeThemePreferencesToStorage } from './storage';
import {
  type EffectiveTheme,
  type ThemeMode,
  type ThemePreferences,
} from './types';

interface UseThemePreferencesOptions {
  userId?: string;
  debounceMs?: number;
  onSyncError?: () => void;
}

const withUpdatedAt = (preferences: ThemePreferences): ThemePreferences => ({
  ...preferences,
  version: preferences.version || THEME_PREFERENCES_VERSION,
  updatedAt: new Date().toISOString(),
});

export const resolveEffectiveTheme = (themeMode: ThemeMode, prefersDark: boolean): EffectiveTheme => {
  if (themeMode === 'auto') {
    return prefersDark ? 'dark' : 'light';
  }
  return themeMode;
};

export const applyThemeToDocument = (effectiveTheme: EffectiveTheme) => {
  const root = document.documentElement;
  root.dataset.theme = effectiveTheme;
  root.classList.toggle('dark', effectiveTheme === 'dark');
  root.style.colorScheme = effectiveTheme;
};

export const getSystemPrefersDark = (): boolean => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
};

export const initializeThemeOnBootstrap = () => {
  const cached = readThemePreferencesFromStorage();
  const effective = resolveEffectiveTheme(cached.themeMode, getSystemPrefersDark());
  applyThemeToDocument(effective);
  return cached;
};

export const useThemePreferences = ({
  userId,
  debounceMs = 500,
  onSyncError,
}: UseThemePreferencesOptions) => {
  const [preferences, setPreferences] = useState<ThemePreferences>(() => readThemePreferencesFromStorage());
  const [isHydrated, setIsHydrated] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [systemPrefersDark, setSystemPrefersDark] = useState<boolean>(() => getSystemPrefersDark());
  const localMutationRef = useRef(false);
  const syncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingSyncRef = useRef<ThemePreferences | null>(null);

  const applyPreferences = useCallback((next: ThemePreferences, markAsMutated: boolean) => {
    const normalized = writeThemePreferencesToStorage(withUpdatedAt(next));
    setPreferences(normalized);
    if (markAsMutated) {
      localMutationRef.current = true;
    }
    return normalized;
  }, []);

  const syncToBackend = useCallback(
    async (next: ThemePreferences) => {
      if (!userId) {
        return;
      }
      setIsSyncing(true);
      try {
        const saved = await themePreferencesService.update(next);
        applyPreferences(saved, false);
        pendingSyncRef.current = null;
      } catch {
        onSyncError?.();
      } finally {
        setIsSyncing(false);
      }
    },
    [applyPreferences, onSyncError, userId]
  );

  const scheduleSync = useCallback(
    (next: ThemePreferences) => {
      pendingSyncRef.current = next;
      if (syncTimerRef.current) {
        clearTimeout(syncTimerRef.current);
      }
      syncTimerRef.current = setTimeout(() => {
        if (pendingSyncRef.current) {
          void syncToBackend(pendingSyncRef.current);
        }
      }, debounceMs);
    },
    [debounceMs, syncToBackend]
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (event: MediaQueryListEvent) => {
      setSystemPrefersDark(event.matches);
    };
    mediaQuery.addEventListener('change', handler);
    return () => {
      mediaQuery.removeEventListener('change', handler);
    };
  }, []);

  useEffect(() => {
    applyThemeToDocument(resolveEffectiveTheme(preferences.themeMode, systemPrefersDark));
  }, [preferences.themeMode, systemPrefersDark]);

  useEffect(() => {
    const cached = readThemePreferencesFromStorage();
    setPreferences(cached);
    setIsHydrated(true);

    if (!userId) {
      return;
    }

    localMutationRef.current = false;
    let cancelled = false;

    const loadFromBackend = async () => {
      try {
        const remote = await themePreferencesService.get();
        if (cancelled || localMutationRef.current) {
          return;
        }
        applyPreferences(remote, false);
      } catch {
        return;
      }
    };

    void loadFromBackend();

    return () => {
      cancelled = true;
    };
  }, [applyPreferences, userId]);

  useEffect(() => {
    return () => {
      if (syncTimerRef.current) {
        clearTimeout(syncTimerRef.current);
      }
    };
  }, []);

  const setThemeMode = useCallback(
    (themeMode: ThemeMode) => {
      const next = applyPreferences(
        {
          ...preferences,
          themeMode,
        },
        true
      );
      scheduleSync(next);
    },
    [applyPreferences, preferences, scheduleSync]
  );

  const effectiveTheme = useMemo(
    () => resolveEffectiveTheme(preferences.themeMode, systemPrefersDark),
    [preferences.themeMode, systemPrefersDark]
  );

  const retryPendingSync = useCallback(() => {
    if (pendingSyncRef.current) {
      void syncToBackend(pendingSyncRef.current);
    }
  }, [syncToBackend]);

  return {
    preferences,
    themeMode: preferences.themeMode,
    effectiveTheme,
    isHydrated,
    isSyncing,
    setThemeMode,
    retryPendingSync,
  };
};
